/* @refresh skip */
// IE Hub - Route Configuration
// Main routing configuration for the application

import { lazy } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';
import CheckColor from '@/pages/CheckColor';
import AuthScreen from '@/pages/public/authScreen';
import NewOnBoardingScreen from '@/pages/private/onBoarding/newOnBoardingScreen';

import RequireAuth from './guards/RequireAuth';
import RequireAdminKey from './guards/RequireAdminKey';
import RequireApplicationEditable from './guards/RequireApplicationEditable';
import GuestOnly from './guards/GuestOnly';
import BlockUwFromGenericOnboarding from './guards/BlockUwFromGenericOnboarding';
import BudgetPage from '@/pages/private/dashboard/budgetPage';
import SharePage from '@/pages/private/dashboard/SharePage';
import UpdateBudgetPage from '@/pages/private/noNavButFooter/updateBudgetPage';
import MonthlyBudgetSummary from '@/pages/private/noNavButFooter/montlyBudgetSummary';
import DocumentPage from '@/pages/private/dashboard/DocumentPage';
import VerifyAddressGuard from './guards/VerifyAddressGuard';
import RequireBudgetOnboarding from './guards/RequireBudgetOnboarding';
import ProfilePage from '@/pages/private/fromDashboard/profilePage';
import HelpAndSupportPage from '@/pages/private/fromDashboard/HelpAndSupportPage';
import FaqPage from '@/pages/private/fromDashboard/faqPage';
import OpenBankingPage from '@/pages/private/fromDashboard/OpenBankingPage';
import ObConfirmTransactionPage from '@/pages/private/fromDashboard/obConfirmTransactionPage';
import ObSummaryPage from '@/pages/private/fromDashboard/obSummary';
import ObThankYou from '@/pages/private/fromDashboard/obThankYou';
import ShareSuccessPage from '@/pages/private/fromDashboard/ShareSuccessPage';
import BenefitsCheckPage from '@/pages/private/fromDashboard/BenefitsCheckPage';
import FreePostPage from '@/pages/private/fromDashboard/freePostPage';
import AddCompanyPage from '@/pages/private/fromDashboard/addCompanyPage';

// Lazy load route components
const Dashboard = lazy(() => import('../../pages/private/dashboard/Dashboard'));
const ScreenRoute = lazy(() => import('./ScreenRoute'));
const NotFound = lazy(() => import('../../pages/public/NotFound'));
const NotificationTools = lazy(() => import('../../NotificationTools'));
const JSONScreenDemo = lazy(() => import('./JSONScreenDemo'));
const OnBoarding = lazy(() => import('../../pages/OnBoarding'));
const GoogleCallback = lazy(() => import('./auth/google-callback'));
const VerifyEmail = lazy(() => import('./auth/verify-email.'));
const MagicLinkVerify = lazy(() => import('./auth/magic-link-verify'));
const AcceptTerms = lazy(() => import('../../pages/AcceptTerms'));
const RootRoute = lazy(() => import('./RootRoute'));
const AgentTaskLandingPage = lazy(() => import('../../pages/public/agentTask/AgentTaskLandingPage'));
const DemoMagicLinkPage = lazy(() => import('../../pages/public/DemoMagicLinkPage'));
const VersionPage = lazy(() => import('../../pages/public/VersionPage'));

// TEMP: testpack harness for LMX-Equifax validation. Public, no auth.
// Delete this lazy import + the route entry below + the admin-testpack folder
// when validation work is finished.
const TestPackRunsPage = lazy(() => import('../../pages/admin-testpack/TestPackRunsPage'));

// UW Journey pages
const UWTermsAcceptancePage = lazy(() => import('../../pages/private/uw/UWTermsAcceptancePage'));
const UWOfferSummaryPage = lazy(() => import('../../pages/private/uw/UWOfferSummaryPage'));
const UWOnboardingFlow = lazy(() => import('../../pages/private/uw/UWOnboardingFlow'));
const UWOpenBankingPage = lazy(() => import('../../pages/private/uw/UWOpenBankingPage'));
const UWBudgetPage = lazy(() => import('../../pages/private/uw/budget/UWBudgetPage'));
const UWInfoRequestPage = lazy(() => import('../../pages/private/uw/requests/UWInfoRequestPage'));
const UWDocRequestPage = lazy(() => import('../../pages/private/uw/requests/UWDocRequestPage'));
const UWKycReviewPage = lazy(() => import('../../pages/private/uw/UWKycReviewPage'));
const UWOnfidoKycPage = lazy(() => import('../../pages/private/uw/UWOnfidoKycPage'));
const UWSoftCheckLoadingPage = lazy(() => import('../../pages/private/uw/UWSoftCheckLoadingPage'));
const UWSoftCheckSuccessPage = lazy(() => import('../../pages/private/uw/UWSoftCheckSuccessPage'));
const UWSoftCheckOnfidoFailPage = lazy(() => import('../../pages/private/uw/UWSoftCheckOnfidoFailPage'));
const UWSoftCheckLmxFailPage = lazy(() => import('../../pages/private/uw/UWSoftCheckLmxFailPage'));
const UWAffordabilityDeclinePage = lazy(() => import('../../pages/private/uw/UWAffordabilityDeclinePage'));
const UWDirectDebitPage = lazy(() => import('../../pages/private/uw/UWDirectDebitPage'));
const UWFinalReviewPage = lazy(() => import('../../pages/private/uw/UWFinalReviewPage'));
const UWConfirmationPage = lazy(() => import('../../pages/private/uw/UWConfirmationPage'));
const UWDecisionPage = lazy(() => import('../../pages/private/uw/UWDecisionPage'));
const UWExpiredPage = lazy(() => import('../../pages/private/uw/UWExpiredPage'));
const UWLayout = lazy(() => import('../../pages/private/uw/UWRouteLayout'));
const UWWebhookLogsPage = lazy(() => import('../../pages/private/uw/UWWebhookLogsPage'));
const ApiFailuresPage = lazy(() => import('../../pages/private/admin/ApiFailuresPage'));

function SignupRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/auth${search}`} replace />;
}

function UWTermsRedirect() {
  const { applicationId } = useParams<{ applicationId: string }>();
  return <Navigate to={`/uw/offer/${applicationId}`} replace />;
}

// Route configuration
export const routes = [
  // Root route (handles scode parameter)
  {
    path: '/',
    element: <RootRoute />,
    errorElement: <ErrorBoundary><div /></ErrorBoundary>
  },

  // Private routes (require authentication)
  {
    element: <RequireAuth />,
    errorElement: <ErrorBoundary><div /></ErrorBoundary>,
    children: [
      // Dashboard explicit path
   
      // All customer-style screens (anything using the customer Header with
      // Home/Budget/Documents/Share tabs) are wrapped with
      // BlockUwFromGenericOnboarding. UW customers must never see the
      // customer-shell screens — the guard detects UW status and redirects
      // to their /uw/* journey. Non-UW users pass through unchanged.
      {
        element: <BlockUwFromGenericOnboarding />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>,
        children: [
          {
            path: 'onboarding',
            element: <OnBoarding />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
          {
            path: 'new-onboarding',
            element: <NewOnBoardingScreen />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
          // Restrict onboarding guard to budget, documents, and share pages
          {
            element: <RequireBudgetOnboarding />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>,
            children: [
              {
                path: 'budget',
                element: <BudgetPage />,
                errorElement: <ErrorBoundary><div /></ErrorBoundary>
              },
              {
                path: 'documents',
                element: <DocumentPage />,
                errorElement: <ErrorBoundary><div /></ErrorBoundary>
              },
              {
                path: 'share',
                element: <SharePage />,
                errorElement: <ErrorBoundary><div /></ErrorBoundary>
              },
            ]
          },
          {
            path: 'dashboard',
            element: <Dashboard />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
          {
            path: 'share-success',
            element: <ShareSuccessPage />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
          {
            path: 'freepost',
            element: <FreePostPage />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
          {
            path: 'addcompany',
            element: <AddCompanyPage />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
          {
            path: 'profile',
            element: <ProfilePage />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
          {
            path: 'help-and-support',
            element: <HelpAndSupportPage />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
          {
            path: 'faqs',
            element: <FaqPage />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
          {
            path: 'open-banking',
            element: <OpenBankingPage />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
          {
            path: 'ob-summary',
            element: <ObSummaryPage />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
          {
            path: 'ob-thank-you',
            element: <ObThankYou />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
          {
            path: 'benefits',
            element: <BenefitsCheckPage />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
          {
            path: 'update-budget',
            element: <UpdateBudgetPage />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
          {
            path: 'monthly-budget-summary',
            element: <MonthlyBudgetSummary />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
          {
            path: 'verify-address',
            element: <VerifyAddressGuard />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
        ]
      },
      // Bank-redirect landing for Open Banking. Sits outside
      // BlockUwFromGenericOnboarding because obConfirmTransactionPage runs
      // its own UW handoff (reads uw_ob_returnPath from localStorage and
      // forwards to /uw/open-banking/<appId>?phase=polling preserving the
      // SearchID). Letting the generic UW guard fire here would strip the
      // SearchID and bounce users via /uw/offer → /uw/budget on subsequent
      // bank adds, skipping the confirm-transactions screen.
      {
        path: 'obq',
        element: <ObConfirmTransactionPage />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      {
        path: 'ob-confirm-transaction',
        element: <ObConfirmTransactionPage />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      {
        path: 'accept-terms',
        element: <AcceptTerms />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      // Dynamic screen routes
      {
        path: 'screen/:schemaId',
        element: <ScreenRoute />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      // Tools and demos
      {
        path: 'notifications',
        element: <NotificationTools />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      {
        path: 'demo',
        element: <JSONScreenDemo />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      {
        path: 'color-check',
        element: <CheckColor />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },

      // ── UW Journey Routes ──
      // Pages WITHOUT the shell (own layout)
      {
        path: 'uw/terms/:applicationId',
        element: <UWTermsRedirect />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      {
        path: 'uw/offer/:applicationId',
        element: <UWOfferSummaryPage />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      {
        element: <RequireApplicationEditable />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>,
        children: [
          {
            path: 'uw/onboarding/:applicationId',
            element: <UWOnboardingFlow />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          }
        ]
      },
      {
        path: 'uw/expired/:applicationId',
        element: <UWExpiredPage />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },

      // Pages WITH the shell (header + stepper + footer)
      {
        path: 'uw',
        element: <UWLayout />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>,
        children: [
          {
            element: <RequireApplicationEditable />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>,
            children: [
              {
                path: 'kyc-review/:applicationId',
                element: <UWKycReviewPage />,
                errorElement: <ErrorBoundary><div /></ErrorBoundary>
              },
              {
                path: 'id-upload/:applicationId',
                element: <UWOnfidoKycPage />,
                errorElement: <ErrorBoundary><div /></ErrorBoundary>
              },
              // ── Phase 1 Soft Check (LMX/Equifax) — between Onfido and OB ──
              // Only the loading screen is guarded — terminal sub-pages below
              // render status-bound content on first legitimate visit and must
              // not be intercepted.
              {
                path: 'soft-check/:applicationId',
                element: <UWSoftCheckLoadingPage />,
                errorElement: <ErrorBoundary><div /></ErrorBoundary>
              },
            ]
          },
          {
            path: 'soft-check/:applicationId/success',
            element: <UWSoftCheckSuccessPage />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
          {
            path: 'soft-check/:applicationId/onfido-fail',
            element: <UWSoftCheckOnfidoFailPage />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
          {
            path: 'soft-check/:applicationId/decline',
            element: <UWSoftCheckLmxFailPage />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
          {
            // Affordability hard-stop from I&E summary (Total leftover <= £0). Terminal
            // screen — deliberately outside RequireApplicationEditable so it still renders
            // if the application is moved to a non-editable state.
            path: 'affordability-decline/:applicationId',
            element: <UWAffordabilityDeclinePage />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
          {
            element: <RequireApplicationEditable />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>,
            children: [
              {
                path: 'open-banking/:applicationId',
                element: <UWOpenBankingPage />,
                errorElement: <ErrorBoundary><div /></ErrorBoundary>
              },
              {
                path: 'budget/:applicationId',
                element: <UWBudgetPage />,
                errorElement: <ErrorBoundary><div /></ErrorBoundary>
              },
              {
                path: 'direct-debit/:applicationId',
                element: <UWDirectDebitPage />,
                errorElement: <ErrorBoundary><div /></ErrorBoundary>
              },
              {
                path: 'review/:applicationId',
                element: <UWFinalReviewPage />,
                errorElement: <ErrorBoundary><div /></ErrorBoundary>
              },
            ]
          },
          {
            path: 'submitted/:applicationId',
            element: <UWConfirmationPage />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
          {
            path: 'decision/:applicationId',
            element: <UWDecisionPage />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
          {
            path: 'info-request/:requestId',
            element: <UWInfoRequestPage />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
          {
            path: 'doc-request/:requestId',
            element: <UWDocRequestPage />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
        ]
      },
    ]
  },

  // Guest routes (public access)
  {
    path: 'auth',
    element: <GuestOnly><AuthScreen /></GuestOnly>,
    errorElement: <ErrorBoundary><div /></ErrorBoundary>
  },
  {
    path: 'auth/google-callback',
    element: <GoogleCallback />,
    errorElement: <ErrorBoundary><div /></ErrorBoundary>
  },
  {
    path: 'auth/verify-email',
    element: <VerifyEmail />,
    errorElement: <ErrorBoundary><div /></ErrorBoundary>
  },
  {
    path: 'auth/magic-link',
    element: <MagicLinkVerify />,
    errorElement: <ErrorBoundary><div /></ErrorBoundary>
  },

  // Public agent-task magic-link landing (no auth, token in URL)
  {
    path: 'agent-task/:token',
    element: <AgentTaskLandingPage />,
    errorElement: <ErrorBoundary><div /></ErrorBoundary>
  },

  // Public demo magic-link generator (shareable URL)
  {
    path: 'demo-link',
    element: <DemoMagicLinkPage />,
    errorElement: <ErrorBoundary><div /></ErrorBoundary>
  },

  // Public version/diagnostics page — shows FE + BE version numbers.
  {
    path: 'version',
    element: <VersionPage />,
    errorElement: <ErrorBoundary><div /></ErrorBoundary>
  },

  // TEMP: testpack harness — public, no auth. Visit /#/testpack-runs.
  // Delete this entry + the lazy import + the admin-testpack folder when done.
  {
    path: 'testpack-runs',
    element: <TestPackRunsPage />,
    errorElement: <ErrorBoundary><div /></ErrorBoundary>
  },

  // Legacy route support: old /#/signup should land on /#/auth (preserve query)
  {
    path: 'signup',
    element: <SignupRedirect />,
    errorElement: <ErrorBoundary><div /></ErrorBoundary>
  },

  // ── Admin (secret-key gated): debug log viewers ──
  // TOP-LEVEL / PUBLIC route, intentionally NOT under <RequireAuth /> so the
  // console is reachable with ONLY the shared admin key — no login required.
  // Access is gated client-side by <RequireAdminKey /> (key + host allow-list)
  // and server-side by requireAdminKey. Both pages expose sensitive PII, so the
  // key is the sole credential — keep it a strong, non-default per-env secret.
  {
    element: <RequireAdminKey />,
    errorElement: <ErrorBoundary><div /></ErrorBoundary>,
    children: [
      {
        path: 'admin/webhook-logs',
        element: <UWWebhookLogsPage />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      {
        path: 'admin/api-failures',
        element: <ApiFailuresPage />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
    ]
  },

  // Catch-all for 404
  {
    path: '*',
    element: <NotFound />,
    errorElement: <ErrorBoundary><div /></ErrorBoundary>
  }
];

export default routes;